import { ChangeDetectorRef, Component, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { CompaniesService } from '@services/companies.service';
import { EMPTY, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, finalize, switchMap, take, takeUntil } from 'rxjs/operators';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { UserModel } from '@data-layer/models/user';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { InputPhoneComponent } from '@shared/input-phone/components';
import { AlertService } from '@services/alert.service';
import { IorMatchGateHost, IorMatchGateService } from '@services/ior-match-gate.service';
import { ImporterOfRecordContactInformationModel } from '@data-layer/models/companies';
import { DigitalSignModel, DigitalSignsRepository } from '@data-layer/repositories/digital-signs.repository';
import { enumDigitalSignState } from '@data-layer/enums/digital-signs/enumDigitalSignState';
import { IorContactMatchHelper } from '@helpers/ior-contact-match.helper';

@Component({
  selector: 'company-ior-contact-form',
  templateUrl: './company-ior-contact-form.component.html',
  styleUrls: ['./company-ior-contact-form.component.scss'],
})
export class CompanyIorContactFormComponent implements OnInit, OnDestroy, IorMatchGateHost {
  @ViewChild(InputPhoneComponent, { static: false }) public modalInputPhone: InputPhoneComponent;

  @Input() public formGroup: UntypedFormGroup;
  @Input() public isShowDescription: boolean = false;

  public isPhoneModalOpen: boolean = false;
  public isPhoneSaving: boolean = false;
  public isMatchConfirmOpen: boolean = false;
  public phoneFormControl: UntypedFormControl;
  public isPoaSigned: boolean = false;
  public readonly CUSTOM_MASK: typeof CUSTOM_MASK = CUSTOM_MASK;

  // #13175 — `iorMatchesUser` renders the standard red error text under the field (set by
  // setMatchError when the IOR email/phone equals the current user's).
  public errors: { [name: string]: IErrorForm } = {
    firstName: { required: 'FORM_ERROR.REQUIRED' },
    lastName: { required: 'FORM_ERROR.REQUIRED' },
    phoneNumber: {
      required: 'FORM_ERROR.REQUIRED',
      validatePhoneNumber: 'FORM_ERROR.PHONE_NUMBER',
      iorMatchesUser: 'COMPANY.IOR.PHONE_MATCHES_USER_HINT',
    },
    emailAddress: {
      required: 'FORM_ERROR.REQUIRED',
      invalidEmail: 'FORM_ERROR.LOGIN_EMAIL',
      email: 'FORM_ERROR.LOGIN_EMAIL',
      iorMatchesUser: 'COMPANY.IOR.EMAIL_MATCHES_USER_HINT',
    },
  };

  private destroy$: ReplaySubject<boolean> = new ReplaySubject();
  private pendingMatchGate$: ReplaySubject<boolean> | null = null;
  // #13175 — true when the company already has a signed/paid POA: existing users are
  // grandfathered and the IOR match validation is suppressed for them. Starts true so a
  // grandfathered user never sees a red flash before the async digital-signs call resolves;
  // the create flow (no companyId) resets it to false synchronously in ngOnInit.
  private isGrandfathered: boolean = true;

  constructor(
    private companiesService: CompaniesService,
    private cdr: ChangeDetectorRef,
    private alertService: AlertService,
    private iorMatchGate: IorMatchGateService,
    private digitalSignsRepository: DigitalSignsRepository,
  ) {}

  public ngOnInit(): void {
    this.iorMatchGate.register(this);
    this.companiesService.companyUpdated$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cdr.markForCheck());

    // Subscribe to isImporterOfRecord changes to toggle field state
    this.formGroup
      .get('isImporterOfRecord')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((isChecked: boolean) => {
        this.handleCheckboxChange(isChecked);
      });

    // #13175 — recompute the IOR match on every email/phone change (raw flags update live;
    // the red display stays gated by `touched`, so nothing turns red mid-typing before blur).
    this.iorFormGroup
      ?.get('emailAddress')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.recomputeIorMatch());
    this.iorFormGroup
      ?.get('phoneNumber')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.recomputeIorMatch());

    // Initialize state based on current value — don't trigger modal on init
    // IOR fields are already populated from company.importerOfRecordContactInformation via createCompanyForm()
    const currentValue = this.formGroup.get('isImporterOfRecord')?.value;
    if (currentValue) {
      const user: UserModel = this.companiesService.getUser();
      if (user?.phoneNumber) {
        // Just disable fields — keep company IOR data as-is (don't overwrite with user data)
        this.iorFormGroup?.disable();
        this.cdr.markForCheck();
      } else {
        // User has no phone — uncheck silently so user must manually check (which will trigger modal)
        this.formGroup.get('isImporterOfRecord')?.setValue(false, { emitEvent: false });
      }
    }

    // #13160 — lock the IOR email once the company's POA is synced from Shell
    // (latest digital-sign record is Completed). Latest = max by id (no createdDate on the model).
    const companyId: number = this.formGroup.get('id')?.value;
    if (companyId != null) {
      this.digitalSignsRepository
        .list(companyId)
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe((items: DigitalSignModel[]) => {
          const last: DigitalSignModel = (items || []).sort((a: DigitalSignModel, b: DigitalSignModel) => b.id - a.id)[0];
          this.isPoaSigned = !!last && last.state === enumDigitalSignState.Completed;
          this.isGrandfathered = IorContactMatchHelper.isPoaGrandfathered(last);
          this.applyPoaLock();
          // Validate on open: surface red immediately when the saved IOR data already matches.
          this.markPrefilledMatchTouched();
          this.recomputeIorMatch();
          this.cdr.markForCheck();
        });
    } else {
      // New company: no POA yet, so validation always applies.
      this.isGrandfathered = false;
      this.recomputeIorMatch();
    }
  }

  public ngOnDestroy(): void {
    this.iorMatchGate.unregister(this);
    this.resolvePendingMatchGate(true);
    this.destroy$.next();
  }

  @HostListener('document:keydown.escape')
  public onEscKeydown(): void {
    if (this.isPhoneModalOpen && !this.isPhoneSaving) {
      this.onPhoneModalClose();
    }
  }

  public get iorFormGroup(): UntypedFormGroup {
    return this.formGroup.get('importerOfRecordContactInformation') as UntypedFormGroup;
  }

  public trimField(fieldName: string): void {
    const control = this.iorFormGroup?.get(fieldName);
    if (control && typeof control.value === 'string') {
      control.setValue(control.value.trim());
    }
  }

  public onEmailBlur(): void {
    const control = this.iorFormGroup?.get('emailAddress');
    if (!control || control.disabled) {
      return;
    }

    // Trim first (emitEvent:false — recomputeIorMatch is called explicitly below)
    const trimmedValue = (control.value || '').trim();
    control.setValue(trimmedValue, { emitEvent: false });

    control.markAsTouched();
    this.recomputeIorMatch();
  }

  public onPhoneModalClose(): void {
    this.isPhoneModalOpen = false;
    this.resolvePendingMatchGate(true);
    this.cdr.markForCheck();
  }

  public onPhoneModalSave(): void {
    if (!this.isPhoneValid()) {
      this.phoneFormControl?.markAsTouched();
      this.modalInputPhone?.inputFormControl?.markAsTouched();
      this.cdr.markForCheck();
      return;
    }

    const phoneValue = this.phoneFormControl.value ? String(this.phoneFormControl.value).replace(/(\s|-)*/g, '') : '';

    const user: UserModel = this.companiesService.getUser();
    this.isPhoneSaving = true;

    this.companiesService
      .updateUser({ ...user, phoneNumber: phoneValue })
      .pipe(
        switchMap(() => this.companiesService.getUserData(true)),
        catchError(() => {
          this.alertService.error('LABELS.SOMETHING_DONT_WORK_MESSAGE');
          return EMPTY;
        }),
        finalize(() => {
          this.isPhoneSaving = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.isPhoneModalOpen = false;
        this.formGroup.get('isImporterOfRecord')?.setValue(true, { emitEvent: false });
        this.applyUserToIor();
        // Checkbox set via emitEvent:false bypasses handleCheckboxChange — refresh flags here.
        this.recomputeIorMatch();
        this.resolvePendingMatchGate(true);
      });
  }

  public confirmIorMatchOrPass(): Observable<boolean> {
    if (this.formGroup.get('isImporterOfRecord')?.value) {
      return of(true);
    }
    // Skip when IOR fields haven't been touched — avoids false alarms on edit
    // when saved IOR data already matches the user but only unrelated fields changed.
    if (this.iorFormGroup?.pristine) {
      return of(true);
    }
    if (!this.isMatchingCurrentUser()) {
      return of(true);
    }
    this.pendingMatchGate$ = new ReplaySubject<boolean>(1);
    this.isMatchConfirmOpen = true;
    this.cdr.markForCheck();
    return this.pendingMatchGate$.asObservable();
  }

  public onIorMatchAccept(): void {
    this.isMatchConfirmOpen = false;
    const user: UserModel = this.companiesService.getUser();
    if (user?.phoneNumber) {
      this.formGroup.get('isImporterOfRecord')?.setValue(true, { emitEvent: false });
      this.applyUserToIor();
      // User confirmed they are the IOR — match validation no longer applies.
      this.recomputeIorMatch();
      this.resolvePendingMatchGate(true);
      return;
    }
    this.openPhoneRequiredModal();
  }

  public onIorMatchDecline(): void {
    if (this.isPhoneModalOpen) {
      // Phone modal owns the gate now (no-phone Accept branch). Skip resolution.
      return;
    }
    this.isMatchConfirmOpen = false;
    // #13175 — user denied being the IOR but the data matches: save proceeds, yet the
    // matching email/phone must show as invalid (red border + red error text) right away and on re-entry.
    const iorGroup: UntypedFormGroup = this.iorFormGroup;
    if (iorGroup) {
      iorGroup.get('emailAddress')?.markAsTouched();
      iorGroup.get('phoneNumber')?.markAsTouched();
    }
    this.recomputeIorMatch();
    this.resolvePendingMatchGate(true);
  }

  private handleCheckboxChange(isChecked: boolean): void {
    const iorGroup = this.iorFormGroup;
    if (!iorGroup) {
      return;
    }

    if (isChecked) {
      const user: UserModel = this.companiesService.getUser();

      if (!user?.phoneNumber) {
        this.formGroup.get('isImporterOfRecord')?.setValue(false, { emitEvent: false });
        this.openPhoneRequiredModal();
        return;
      }

      this.applyUserToIor();
    } else {
      iorGroup.patchValue({
        firstName: '',
        lastName: '',
        phoneNumber: '',
        emailAddress: '',
      });
      iorGroup.enable();
    }
    this.applyPoaLock();
    this.recomputeIorMatch();
    this.cdr.markForCheck();
  }

  private applyPoaLock(): void {
    if (!this.isPoaSigned) {
      return;
    }
    // #13160 — after POA is signed: email is read-only and the current user can't be made the IOR.
    // emitEvent:false on the checkbox prevents handleCheckboxChange() from wiping manual name/phone data.
    this.iorFormGroup?.get('emailAddress')?.disable({ emitEvent: false });
    this.formGroup.get('isImporterOfRecord')?.disable({ emitEvent: false });
  }

  private applyUserToIor(): void {
    const iorGroup = this.iorFormGroup;
    if (!iorGroup) {
      return;
    }
    const user: UserModel = this.companiesService.getUser();
    iorGroup.patchValue({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phoneNumber: user?.phoneNumber || '',
      emailAddress: user?.email || '',
    });
    iorGroup.disable();
    this.formGroup.get('phone')?.patchValue(user?.phoneNumber || '');
    this.cdr.markForCheck();
  }

  public isPhoneValid(): boolean {
    const hasValue = !!this.phoneFormControl?.value;
    const isInternalValid = this.modalInputPhone?.inputFormControl ? !this.modalInputPhone.inputFormControl.invalid : false; // Keep disabled until ViewChild resolves and validation completes
    return hasValue && isInternalValid;
  }

  private openPhoneRequiredModal(): void {
    this.phoneFormControl = new UntypedFormControl('', [Validators.required]);
    this.phoneFormControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cdr.markForCheck();
    });
    this.isPhoneModalOpen = true;
    this.cdr.markForCheck();

    // Subscribe to internal validation after ViewChild resolves
    setTimeout(() => {
      this.modalInputPhone?.inputFormControl?.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.cdr.markForCheck();
      });
    }, 0);
  }

  private isMatchingCurrentUser(): boolean {
    const user: UserModel = this.companiesService.getUser();
    if (!user) {
      return false;
    }

    const userFirst: string = IorContactMatchHelper.normalizeText(user.firstName);
    const userLast: string = IorContactMatchHelper.normalizeText(user.lastName);
    const userEmail: string = IorContactMatchHelper.normalizeText(user.email);
    const userPhone: string = IorContactMatchHelper.normalizePhone(user.phoneNumber);

    // Avoid false positives when the current user profile is effectively blank.
    if (userFirst === '' && userLast === '' && userEmail === '' && userPhone === '') {
      return false;
    }

    const ior: ImporterOfRecordContactInformationModel =
      (this.iorFormGroup?.getRawValue() as ImporterOfRecordContactInformationModel) || {};
    const iorFirst: string = IorContactMatchHelper.normalizeText(ior.firstName);
    const iorLast: string = IorContactMatchHelper.normalizeText(ior.lastName);
    const iorEmail: string = IorContactMatchHelper.normalizeText(ior.emailAddress);
    const iorPhone: string = IorContactMatchHelper.normalizePhone(ior.phoneNumber);

    if (iorFirst !== userFirst || iorLast !== userLast || iorEmail !== userEmail) {
      return false;
    }

    // Names + email match. Phone may match exactly, OR user has no phone yet but
    // typed a non-blank IOR phone — in both cases prompt the user to confirm.
    if (userPhone !== '' && iorPhone === userPhone) {
      return true;
    }
    if (userPhone === '' && iorPhone !== '') {
      return true;
    }
    return false;
  }

  // #13175 — validation is active only when the user hasn't claimed to be the IOR and the
  // company isn't grandfathered (already signed/paid POA).
  private get isValidationActive(): boolean {
    if (this.formGroup.get('isImporterOfRecord')?.value) {
      return false;
    }
    return !this.isGrandfathered;
  }

  private emailMatchesUser(): boolean {
    const iorGroup: UntypedFormGroup = this.iorFormGroup;
    return !!iorGroup && IorContactMatchHelper.emailMatchesUser(iorGroup.getRawValue(), this.companiesService.getUser());
  }

  private phoneMatchesUser(): boolean {
    const iorGroup: UntypedFormGroup = this.iorFormGroup;
    return !!iorGroup && IorContactMatchHelper.phoneMatchesUser(iorGroup.getRawValue(), this.companiesService.getUser());
  }

  // #13175 — set/clear the `iorMatchesUser` error on email/phone. This makes the fields
  // genuinely invalid → red border + red error text under the field (via app-control-errors,
  // since the key is in the `errors` map), the "Missing or invalid" popup on Save, POA block
  // (formGroup.valid) and exclusion from auto-validation.
  private recomputeIorMatch(): void {
    const iorGroup: UntypedFormGroup = this.iorFormGroup;
    if (!iorGroup) {
      return;
    }
    const active: boolean = this.isValidationActive;
    this.setMatchError(iorGroup.get('emailAddress'), active && this.emailMatchesUser());
    this.setMatchError(iorGroup.get('phoneNumber'), active && this.phoneMatchesUser());
    this.cdr.markForCheck();
  }

  // Adds/removes the `iorMatchesUser` error, preserving native required/email errors.
  // On a value change Angular re-runs the field validators and replaces `errors` (dropping our
  // key); valueChanges → recomputeIorMatch then re-applies it. No recursion: setErrors emits
  // statusChanges (which input-phone/control-errors need to repaint), NOT valueChanges — and
  // recomputeIorMatch is wired to valueChanges only. The hasMatch===hadError guard is the backstop.
  private setMatchError(control: AbstractControl | null | undefined, hasMatch: boolean): void {
    if (!control) {
      return;
    }
    const hadError: boolean = control.hasError('iorMatchesUser');
    if (hasMatch === hadError) {
      return;
    }
    if (hasMatch) {
      control.setErrors({ ...(control.errors || {}), iorMatchesUser: true });
      return;
    }
    // Rebuild errors without iorMatchesUser (avoid `delete` per tslint no-dynamic-delete),
    // preserving native required/email errors.
    const current: ValidationErrors = control.errors || {};
    const remaining: ValidationErrors = {};
    Object.keys(current).forEach((key: string) => {
      if (key !== 'iorMatchesUser') {
        remaining[key] = current[key];
      }
    });
    control.setErrors(Object.keys(remaining).length > 0 ? remaining : null);
  }

  // On open, mark the matching fields as touched so the red border shows without interaction.
  // onlySelf: true — touch only the control, NOT the parent formGroup. Otherwise formGroup.touched
  // becomes true on load and EditCompanyComponent.cancelEditableBlock pops the "leave section /
  // save changes" modal the moment the user enters edit mode (#13175 regression).
  private markPrefilledMatchTouched(): void {
    const iorGroup: UntypedFormGroup = this.iorFormGroup;
    if (!this.isValidationActive || !iorGroup) {
      return;
    }
    if (this.emailMatchesUser()) {
      iorGroup.get('emailAddress')?.markAsTouched({ onlySelf: true });
    }
    if (this.phoneMatchesUser()) {
      iorGroup.get('phoneNumber')?.markAsTouched({ onlySelf: true });
    }
  }

  private resolvePendingMatchGate(value: boolean): void {
    if (!this.pendingMatchGate$) {
      return;
    }
    this.pendingMatchGate$.next(value);
    this.pendingMatchGate$.complete();
    this.pendingMatchGate$ = null;
  }
}
